import { Autocomplete, Checkbox, FormControlLabel, Grid, TextField } from '@mui/material'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import { Office, OfficeType, useFetchAllOfficesLazyQuery } from '../../../../generated'
import { GRAPHQL_QUERY_POLICY_CACHE_FIRST, QUERY_PARAMS } from '../../../constants'
import { AppContext } from '../../../context'
import { ActionType } from '../../../context/AppContextReducer'
import { DateRanges } from '../../entries/EntryFilters'
import { AccountsDropdown } from '../../common/ChartOfAccountsDropdown'
import MatterDropdown from '../../common/MatterDropdown'
import { SearchWithKeypress } from '../../common/SearchWithKeypress'

interface PaymentFiltersProps {
  loading: boolean
}

const PaymentFilters = ({ loading }: PaymentFiltersProps) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const setParams = (key: QUERY_PARAMS, value: string) => {
    searchParams.set(key, value)
    setSearchParams(searchParams)
  }
  const getFilterValues = (key: QUERY_PARAMS) => searchParams.get(key) || QUERY_PARAMS.ALL

  // Matter filter. MatterDropdown also writes the matter number into `keyword`; the payment search
  // lives in its own `search` param, so that write does not leak into the search box.
  // Missing params read as '' (not 'all') so the dropdown never looks a matter up by the word "all".
  const { dispatch } = useContext(AppContext)
  const getMatterFilterValues = useCallback((key: QUERY_PARAMS) => searchParams.get(key) || '', [searchParams])
  const setMatterParams = (key: QUERY_PARAMS, value: string) => {
    // Results shrink to one matter, so stay on page 1 rather than an out-of-range page.
    if (key === QUERY_PARAMS.MATTER) dispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    setParams(key, value)
  }

  // Slip Number filter
  const slipNumberParam = searchParams.get(QUERY_PARAMS.SLIP_NUMBER) || ''
  const [slipNumberInput, setSlipNumberInput] = useState(slipNumberParam)
  const [debouncedSlipNumber] = useDebounce(slipNumberInput, 500)

  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    if (debouncedSlipNumber) {
      next.set(QUERY_PARAMS.SLIP_NUMBER, debouncedSlipNumber)
    } else {
      next.delete(QUERY_PARAMS.SLIP_NUMBER)
    }
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSlipNumber])

  // Office / Location filter
  const officeIdParam = searchParams.get(QUERY_PARAMS.OFFICE_ID) || ''
  const [offices, setOffices] = useState<Pick<Office, 'id' | 'name'>[]>([])
  const [officeSearchTerm, setOfficeSearchTerm] = useState('')
  const [debouncedOfficeSearch] = useDebounce(officeSearchTerm, 500)

  const [fetchOffices, { loading: officesLoading }] = useFetchAllOfficesLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,
    onCompleted(data) {
      if (data?.fetchAllOffices?.offices?.length) {
        const filtered = data.fetchAllOffices.offices
          ?.filter(office => office?.type === OfficeType.ProfitCenter)
          ?.map(office => ({ id: office!.id, name: office!.name }))
        setOffices(filtered || [])
      }
    }
  })

  useEffect(() => {
    fetchOffices({
      variables: {
        fetchOfficesInput: {
          keyword: debouncedOfficeSearch
        }
      }
    })
  }, [debouncedOfficeSearch, fetchOffices])

  const selectedOffice = offices.find(o => String(o.id) === officeIdParam) ?? null

  const handleOfficeChange = (newValue: Pick<Office, 'id' | 'name'> | null) => {
    const next = new URLSearchParams(searchParams)
    if (newValue) {
      next.set(QUERY_PARAMS.OFFICE_ID, String(newValue.id))
    } else {
      next.delete(QUERY_PARAMS.OFFICE_ID)
    }
    setSearchParams(next, { replace: true })
  }

  const isReconciledParam = searchParams.get(QUERY_PARAMS.RECONCILED) === 'true'

  const handleReconciledChange = (checked: boolean) => {
    const next = new URLSearchParams(searchParams)
    if (checked) {
      next.set(QUERY_PARAMS.RECONCILED, 'true')
    } else {
      next.delete(QUERY_PARAMS.RECONCILED)
    }
    setSearchParams(next, { replace: true })
  }

  return (
    <Grid container spacing={2} alignItems={'center'}>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <MatterDropdown
          label="Matter"
          includeClosedMatters
          setParams={setMatterParams}
          getFilterValues={getMatterFilterValues}
          sx={{ width: '100%' }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3}>
        <AccountsDropdown
          label="Accounts"
          setParams={setParams}
          accountId={getFilterValues(QUERY_PARAMS.ACCOUNT_ID)}
          isBankAccount={true}
          includeAllActiveAndInactiveAccounts={true}
          fullWidth
        />
      </Grid>

      <Grid item xs={12} md={4} lg={4}>
        <DateRanges
          setParams={setParams}
          getFilterValues={getFilterValues}
          smallTextField={false}
          mediumViewport
          fullWidth
        />
      </Grid>

      <Grid item xs={12} sm={4} md={2} lg={2}>
        <TextField
          label="Slip #"
          value={slipNumberInput}
          onChange={e => setSlipNumberInput(e.target.value)}
          placeholder="Slip #"
          fullWidth
        />
      </Grid>

      <Grid item xs={12} sm={8} md={4} lg={3}>
        <Autocomplete
          options={offices}
          value={selectedOffice}
          onChange={(_, newValue) => handleOfficeChange(newValue)}
          onInputChange={(_, newInputValue, reason) => {
            if (reason === 'input') setOfficeSearchTerm(newInputValue)
            else if (reason === 'clear') setOfficeSearchTerm('')
          }}
          getOptionLabel={option => option.name}
          loading={officesLoading}
          autoHighlight
          autoSelect
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={params => <TextField {...params} label="Location" placeholder="Select Location" />}
          ListboxProps={{ style: { maxHeight: 230, overflowY: 'auto' } }}
          fullWidth
          noOptionsText={officesLoading ? 'Loading...' : 'No Locations'}
        />
      </Grid>

      <Grid item xs={12} sm={8} md={4} lg={3}>
        <SearchWithKeypress searchPlaceholder={'Search Payments'} hasMaxWidth={false} />
      </Grid>

      <Grid item xs={12} sm={4} md={2} lg={'auto'}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isReconciledParam}
              onChange={e => handleReconciledChange(e.target.checked)}
              disabled={loading}
              size="small"
            />
          }
          label="Reconciled"
          sx={{ whiteSpace: 'nowrap' }}
        />
      </Grid>
    </Grid>
  )
}

export default PaymentFilters
